// AddPeopleDrawer — "Add people" per the design. A Drawer with three tabs:
// Add person (three carded sections: PERSONAL INFO, EMPLOYMENT, ACCESS & TAGS
// → POST /company-users/invite-users), Invite by link & QR, and Import users
// (CSV → POST /company-users/import-users, one batch of every ready row).
// Validation mirrors the drawer convention (derived booleans + a `submitted`
// flag); the import tab's file/roles state lives here so the per-tab footer
// can render its counts and submit.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Drawer,
  Field,
  Icon,
  Input,
  PhoneInput,
  SelectDropdown,
  Tabs,
} from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import { useGetRolesQuery } from '@/api/endpoints/rolesApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import { useCreatePersonMutation, useImportUsersMutation } from '@/api/endpoints/peopleApi';
import { useCreatePeopleTagMutation, useGetPeopleTagsQuery } from '@/api/endpoints/tagsApi';
import {
  useGetInviteLinkDetailsQuery,
  useSaveInviteLinkDetailsMutation,
} from '@/api/endpoints/companyApi';
import type { OrgUnit } from '@/mocks/structure';
import { todayPlus } from '@/utils/date';
import { toastApiError } from '@/utils/apiErrorToast';
import { EMPLOYMENT_TYPES, NATIONALITIES } from '../shared/personFields';
import InviteByLinkTab, {
  emptyInviteDraft,
  settingsToDraft,
  buildRequiredFields,
  type InviteDraft,
} from './InviteByLinkTab';
import ImportUsersTab, { effectiveRowRoles, type PickedPeopleCsv } from './ImportUsersTab';
import { rowToCreatePersonInput } from './parsePeopleCsv';

type DrawerTab = 'add-person' | 'invite' | 'import';

interface AddPeopleDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Tab to select when the drawer opens (default 'add-person'). */
  initialTab?: DrawerTab;
}

/** Explicit, because the optional fields are nullable: an inferred object
 *  literal would widen every `null` to `any` under this repo's strict:false. */
interface AddPersonDraft {
  firstName: string;
  lastName: string;
  dialCode: string;
  mobileNumber: string;
  /** Optional fields — null means "not filled in". */
  email: string | null;
  birthDate: string | null; // yyyy-mm-dd (native date input)
  nationality: string | null;
  idNumber: string | null;
  idExpiry: string | null; // yyyy-mm-dd (native date input)
  department: string | null;
  employmentType: string | null;
  roleIds: string[];
  nodeIds: string[];
  tagIds: string[];
}

const emptyDraft: AddPersonDraft = {
  firstName: '',
  lastName: '',
  dialCode: defaultCountry.dial,
  mobileNumber: '',
  email: null,
  birthDate: null,
  nationality: null,
  idNumber: null,
  idExpiry: null,
  department: null,
  employmentType: null,
  roleIds: [],
  nodeIds: [],
  tagIds: [],
};

/** Flatten the org tree to rows carrying their depth for indentation. */
const flattenTree = (units: OrgUnit[], depth = 0): { unit: OrgUnit; depth: number }[] =>
  units.flatMap((unit) => [
    { unit, depth },
    ...(unit.children ? flattenTree(unit.children, depth + 1) : []),
  ]);

/** yyyy-mm-dd (native date input) → ISO datetime; undefined when empty. */
const toIso = (d: string | null) => (d ? new Date(d).toISOString() : undefined);

const AddPeopleDrawer = ({ open, onClose, initialTab = 'add-person' }: AddPeopleDrawerProps) => {
  const { t } = useTranslation();
  const { data: roles = [] } = useGetRolesQuery(undefined, { skip: !open });
  const { data: roots = [] } = useGetStructureSelectionQuery(undefined, { skip: !open });
  const { data: peopleTags = [] } = useGetPeopleTagsQuery(undefined, { skip: !open });
  const levelConfig = useLevelConfig();
  const [createPerson, { isLoading: creating }] = useCreatePersonMutation();
  const [importUsers, { isLoading: importing }] = useImportUsersMutation();
  const [createPeopleTag, { isLoading: addingTag }] = useCreatePeopleTagMutation();
  const [saveInvite, { isLoading: savingInvite }] = useSaveInviteLinkDetailsMutation();

  const [tab, setTab] = useState<DrawerTab>('add-person');
  const [draft, setDraft] = useState(emptyDraft);
  const [submitted, setSubmitted] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newTagOpen, setNewTagOpen] = useState(false);
  // Invite-tab draft (owned here so the per-tab footer can Save/Reset it).
  const [inviteDraft, setInviteDraft] = useState<InviteDraft>(emptyInviteDraft);
  const [inviteSubmitted, setInviteSubmitted] = useState(false);
  // Import-tab state (owned here for the same reason): the parsed file, the
  // shared roles selection, and each row's own role list keyed by file line
  // (seeded from the shared selection on pick; any shared toggle REPLACES
  // every row's list with the new set).
  const [importPicked, setImportPicked] = useState<PickedPeopleCsv | null>(null);
  const [importRoleIds, setImportRoleIds] = useState<string[]>([]);
  const [importRowRoles, setImportRowRoles] = useState<Record<number, string[]>>({});

  // Invite settings + code — fetched when the Invite tab is opened.
  const { data: inviteDetails } = useGetInviteLinkDetailsQuery(undefined, {
    skip: !open || tab !== 'invite',
  });
  // Seed the draft from saved settings once per drawer-open (so switching tabs
  // or a refetch doesn't clobber the user's edits).
  const inviteSeeded = useRef(false);

  // Re-seed each time the drawer opens; discard leftovers on close.
  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setDraft(emptyDraft);
      setSubmitted(false);
      setNewTag('');
      setNewTagOpen(false);
      setInviteDraft(emptyInviteDraft);
      setInviteSubmitted(false);
      inviteSeeded.current = false;
      setImportPicked(null);
      setImportRoleIds([]);
      setImportRowRoles({});
    }
  }, [open, initialTab]);

  // Seed invite settings from the server the first time they arrive.
  useEffect(() => {
    if (!inviteSeeded.current && inviteDetails?.inviteLinkDetails) {
      setInviteDraft(settingsToDraft(inviteDetails.inviteLinkDetails));
      inviteSeeded.current = true;
    }
  }, [inviteDetails]);

  const patch = (changes: Partial<AddPersonDraft>) => setDraft((d) => ({ ...d, ...changes }));
  const invitePatch = (changes: Partial<InviteDraft>) =>
    setInviteDraft((d) => ({ ...d, ...changes }));

  const inviteRolesMissing = inviteDraft.roleIds.length === 0;
  const resetInvite = () => {
    setInviteDraft(emptyInviteDraft);
    setInviteSubmitted(false);
  };
  // Save the invite-link settings — validate roles, then POST.
  const submitInvite = async () => {
    setInviteSubmitted(true);
    if (inviteRolesMissing) return;
    try {
      await saveInvite({
        notifyMeOnJoinCompany: inviteDraft.notify,
        requiredFields: buildRequiredFields(inviteDraft.requestedInfo),
        groups: [],
        companyNodes: inviteDraft.nodeIds,
        roles: inviteDraft.roleIds,
      }).unwrap();
      toast.success(t('invite_saved'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const treeRows = useMemo(() => flattenTree(roots), [roots]);
  // Owner is granted structurally, not picked here — keep it out of the list.
  const selectableRoles = useMemo(() => roles.filter((r) => !r.isOwner), [roles]);

  // ---- Import tab ----
  // The shared selection is AUTHORITATIVE: any change replaces every row's
  // list with the new shared set — per-row adjustments are intentionally
  // discarded (user-specified: they hold only until the next shared change).
  const toggleImportRole = (id: string) => {
    const next = importRoleIds.includes(id)
      ? importRoleIds.filter((x) => x !== id)
      : [...importRoleIds, id];
    setImportRoleIds(next);
    setImportRowRoles((rows) =>
      Object.fromEntries(Object.keys(rows).map((line) => [Number(line), next])),
    );
  };
  // A row's own adjustment edits only that row's list.
  const toggleImportRowRole = (line: number, id: string) =>
    setImportRowRoles((rows) => {
      const current = rows[line] ?? [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...rows, [line]: next };
    });

  const importRows = importPicked?.rows ?? [];
  const importReadyRows = importRows.filter((row) => row.errors.length === 0);
  // Both conditions DISABLE the submit (rather than erroring on click): any
  // flagged row — delete it or replace the file to proceed — and any row
  // without at least one role.
  const importHasErrors = importRows.some((row) => row.errors.length > 0);
  const importRolesMissing = importRows.some(
    (row) => effectiveRowRoles(row.line, importRowRoles).length === 0,
  );
  const importBlocked = importReadyRows.length === 0 || importHasErrors || importRolesMissing;

  // Import every row in one batch (the disabled gate guarantees all rows are
  // error-free and carry ≥1 role by the time this can run).
  const submitImport = async () => {
    if (importBlocked) return;
    try {
      await importUsers({
        users: importReadyRows.map((row) =>
          rowToCreatePersonInput(row, effectiveRowRoles(row.line, importRowRoles)),
        ),
      }).unwrap();
      toast.success(t('people_import_success', { count: importReadyRows.length }));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Every row starts with the shared selection as it stands. Replacing the
  // file re-seeds (old lines belong to the old file), which also discards its
  // per-row adjustments.
  const onImportPicked = (picked: PickedPeopleCsv) => {
    setImportPicked(picked);
    setImportRowRoles(Object.fromEntries(picked.rows.map((row) => [row.line, importRoleIds])));
  };

  // Deleting a row drops it from the preview and the batch (and its role
  // list); deleting the last row returns to the choose-file state.
  const removeImportRow = (line: number) => {
    setImportPicked((picked) => {
      if (!picked) return picked;
      const rows = picked.rows.filter((row) => row.line !== line);
      return rows.length ? { ...picked, rows } : null;
    });
    setImportRowRoles((rows) => {
      const next = { ...rows };
      delete next[line];
      return next;
    });
  };

  const toggle = (key: 'roleIds' | 'nodeIds' | 'tagIds', id: string) =>
    patch({
      [key]: draft[key].includes(id) ? draft[key].filter((x) => x !== id) : [...draft[key], id],
    });

  // Inline tag creation: existing title (case-insensitive) just gets selected;
  // otherwise create via the API, then select the new id.
  const addTag = async () => {
    const title = newTag.trim();
    if (!title) return;
    const existing = peopleTags.find((tag) => tag.title.toLowerCase() === title.toLowerCase());
    if (existing) {
      if (!draft.tagIds.includes(existing.id)) toggle('tagIds', existing.id);
      setNewTag('');
      setNewTagOpen(false);
      return;
    }
    try {
      const created = await createPeopleTag({ title }).unwrap();
      if (created?._id) patch({ tagIds: [...draft.tagIds, created._id] });
      setNewTag('');
      setNewTagOpen(false);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const onNewTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const firstMissing = !draft.firstName.trim();
  const lastMissing = !draft.lastName.trim();
  const phoneMissing = !draft.mobileNumber.trim();
  const rolesMissing = draft.roleIds.length === 0;
  const nodesMissing = draft.nodeIds.length === 0;
  // Birth date is past-only: yesterday is the latest allowed day.
  const maxBirthDate = todayPlus(-1);
  const birthDateInvalid = !!draft.birthDate && draft.birthDate > maxBirthDate;

  const submit = async () => {
    // Required-field validation runs BEFORE any backend call.
    setSubmitted(true);
    if (
      firstMissing ||
      lastMissing ||
      phoneMissing ||
      rolesMissing ||
      nodesMissing ||
      birthDateInvalid
    )
      return;
    try {
      await createPerson({
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        countryCode: draft.dialCode,
        mobileNumber: draft.mobileNumber.trim(),
        email: draft.email?.trim() || undefined,
        roles: draft.roleIds,
        companyStructureNodes: draft.nodeIds,
        tags: draft.tagIds,
        personalIdNumber: draft.idNumber?.trim() || undefined,
        personalIdExpiry: toIso(draft.idExpiry),
        nationality: draft.nationality || undefined,
        birthDate: toIso(draft.birthDate),
        department: draft.department?.trim() || undefined,
        employmentType: draft.employmentType || undefined,
      }).unwrap();
      toast.success(t('add_person_created'));
      onClose();
    } catch (err) {
      toastApiError(err, t);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className={`add-people-drawer${tab === 'import' ? ' add-people-drawer--wide' : ''}`}
      icon='user-plus'
      title={t('add_people_title')}
      subtitle={t('add_people_subtitle')}
      footer={
        tab === 'invite' ? (
          <div className='add-people__footer'>
            <Button variant='ghost' iconStart='rotate-ccw' onClick={resetInvite}>
              {t('invite_reset')}
            </Button>
            <span className='add-people__footer-actions'>
              <Button variant='secondary' onClick={onClose}>
                {t('invite_close')}
              </Button>
              <Button iconStart='check' onClick={submitInvite} loading={savingInvite}>
                {t('invite_save')}
              </Button>
            </span>
          </div>
        ) : tab === 'import' ? (
          <div className='add-people__footer'>
            <span className='add-people__required'>
              {importPicked
                ? t('people_import_rows_summary', {
                    ready: importReadyRows.length,
                    count: importPicked.rows.length,
                  })
                : t('people_import_footer_hint')}
            </span>
            <span className='add-people__footer-actions'>
              <Button variant='secondary' onClick={onClose} disabled={importing}>
                {t('button_cancel')}
              </Button>
              <Button
                iconStart='user-plus'
                onClick={submitImport}
                loading={importing}
                disabled={importBlocked}
              >
                {importReadyRows.length > 0
                  ? t('people_import_submit', { count: importReadyRows.length })
                  : t('people_import_submit_none')}
              </Button>
            </span>
          </div>
        ) : (
          <div className='add-people__footer'>
            <span className='add-people__required'>{t('add_people_required_hint')}</span>
            <span className='add-people__footer-actions'>
              <Button variant='secondary' onClick={onClose} disabled={creating}>
                {t('button_cancel')}
              </Button>
              <Button iconStart='user-plus' onClick={submit} loading={creating}>
                {t('add_person_submit')}
              </Button>
            </span>
          </div>
        )
      }
    >
      <Tabs
        aria-label={t('add_people_title')}
        value={tab}
        onChange={(v) => setTab(v as DrawerTab)}
        items={[
          { value: 'add-person', label: t('add_person_tab_person'), icon: 'user-plus' },
          { value: 'invite', label: t('add_person_tab_invite'), icon: 'link' },
          { value: 'import', label: t('add_person_tab_import'), icon: 'file-text' },
        ]}
      />

      <div className='add-people-drawer__body'>
        {tab === 'import' ? (
          <ImportUsersTab
            picked={importPicked}
            onPicked={onImportPicked}
            roles={selectableRoles}
            roleIds={importRoleIds}
            onToggleRole={toggleImportRole}
            rowRoles={importRowRoles}
            onToggleRowRole={toggleImportRowRole}
            onRemoveRow={removeImportRow}
          />
        ) : tab === 'invite' ? (
          <InviteByLinkTab
            draft={inviteDraft}
            patch={invitePatch}
            submitted={inviteSubmitted}
            inviteCode={inviteDetails?.inviteCode}
            linkActive={Boolean(inviteDetails?.inviteLinkDetails)}
          />
        ) : (
          <div className='add-person'>
            {/* ---- PERSONAL INFO ---- */}
            <section className='add-person__card'>
              <p className='add-person__card-head'>
                <Icon name='user-round' size={16} />
                {t('add_person_section_personal')}
              </p>

              <div className='add-person__card-body'>
                <div className='add-person__row'>
                  <Field
                    label={t('add_person_firstname')}
                    required
                    error={submitted && firstMissing ? t('add_person_err_firstname') : undefined}
                  >
                    <Input
                      value={draft.firstName}
                      placeholder={t('add_person_firstname_ph')}
                      error={submitted && firstMissing}
                      onChange={(e) => patch({ firstName: e.target.value })}
                    />
                  </Field>
                  <Field
                    label={t('add_person_lastname')}
                    required
                    error={submitted && lastMissing ? t('add_person_err_lastname') : undefined}
                  >
                    <Input
                      value={draft.lastName}
                      placeholder={t('add_person_lastname_ph')}
                      error={submitted && lastMissing}
                      onChange={(e) => patch({ lastName: e.target.value })}
                    />
                  </Field>
                </div>
                <div className='add-person__row'>
                  <Field
                    label={t('add_person_phone')}
                    required
                    error={submitted && phoneMissing ? t('add_person_err_phone') : undefined}
                  >
                    <PhoneInput
                      countries={countries}
                      dialCode={draft.dialCode}
                      onDialCodeChange={(dial) => patch({ dialCode: dial })}
                      value={draft.mobileNumber}
                      onChange={(digits) => patch({ mobileNumber: digits })}
                      placeholder={t('add_person_phone_ph')}
                      error={submitted && phoneMissing}
                      hideBadge
                    />
                  </Field>
                  <Field label={t('add_person_email')}>
                    <Input
                      type='email'
                      iconStart='mail'
                      value={draft.email ?? ''}
                      placeholder={t('add_person_email_ph')}
                      onChange={(e) => patch({ email: e.target.value || null })}
                    />
                  </Field>
                </div>
                <div className='add-person__row'>
                  <Field
                    label={t('add_person_birthdate')}
                    error={
                      submitted && birthDateInvalid ? t('add_person_err_birthdate') : undefined
                    }
                  >
                    <Input
                      type='date'
                      iconStart='calendar'
                      max={maxBirthDate}
                      error={submitted && birthDateInvalid}
                      value={draft.birthDate ?? ''}
                      onChange={(e) => patch({ birthDate: e.target.value || null })}
                    />
                  </Field>
                  <Field label={t('add_person_nationality')} htmlFor='add-person-nationality'>
                    <SelectDropdown
                      id='add-person-nationality'
                      size='lg'
                      value={draft.nationality}
                      onChange={(next) => patch({ nationality: next })}
                      options={[
                        { value: null, label: t('add_person_nationality_ph') },
                        ...NATIONALITIES.map((n) => ({ value: n, label: n })),
                      ]}
                    />
                  </Field>
                </div>
                <div className='add-person__row'>
                  <Field label={t('add_person_idnumber')}>
                    <Input
                      iconStart='id-card'
                      value={draft.idNumber ?? ''}
                      placeholder={t('add_person_idnumber_ph')}
                      onChange={(e) => patch({ idNumber: e.target.value || null })}
                    />
                  </Field>
                  <Field label={t('add_person_idexpiry')}>
                    <Input
                      type='date'
                      iconStart='calendar'
                      value={draft.idExpiry ?? ''}
                      onChange={(e) => patch({ idExpiry: e.target.value || null })}
                    />
                  </Field>
                </div>
              </div>
            </section>

            {/* ---- EMPLOYMENT ---- */}
            <section className='add-person__card'>
              <p className='add-person__card-head'>
                <Icon name='briefcase' size={16} />
                {t('add_person_section_employment')}
              </p>

              <div className='add-person__card-body'>
                <div className='add-person__row'>
                  <Field label={t('add_person_department')}>
                    <Input
                      value={draft.department ?? ''}
                      placeholder={t('add_person_department_ph')}
                      onChange={(e) => patch({ department: e.target.value || null })}
                    />
                  </Field>
                  <Field label={t('add_person_emptype')} htmlFor='add-person-emptype'>
                    <SelectDropdown
                      id='add-person-emptype'
                      size='lg'
                      value={draft.employmentType}
                      onChange={(next) => patch({ employmentType: next })}
                      options={[
                        { value: null, label: t('add_person_emptype_ph') },
                        ...EMPLOYMENT_TYPES.map((v) => ({ value: v, label: v })),
                      ]}
                    />
                  </Field>
                </div>

                <div className='add-person__section'>
                  <p className='add-person__label'>
                    {t('add_person_structure_label')}
                    <span className='add-person__hint'> · {t('add_person_structure_hint')}</span>
                  </p>
                  <div className='add-person__tree'>
                    {treeRows.map(({ unit, depth }) => {
                      const indent = {
                        paddingInlineStart: `calc(var(--space-3) + ${depth} * var(--space-5))`,
                      };
                      // The company root is the org container, not a placement target.
                      if (unit.level === 0) {
                        return (
                          <div
                            key={unit.id}
                            className='add-person__tree-row add-person__tree-row--static'
                            style={indent}
                          >
                            <Icon
                              name={levelConfig(unit.level).icon}
                              size={16}
                              className='add-person__tree-icon'
                            />
                            <span className='add-person__tree-name'>{unit.name}</span>
                          </div>
                        );
                      }
                      const selected = draft.nodeIds.includes(unit.id);
                      return (
                        <button
                          key={unit.id}
                          type='button'
                          className={`add-person__tree-row${selected ? ' is-selected' : ''}`}
                          aria-pressed={selected}
                          style={indent}
                          onClick={() => toggle('nodeIds', unit.id)}
                        >
                          <Icon
                            name={levelConfig(unit.level).icon}
                            size={16}
                            className='add-person__tree-icon'
                          />
                          <span className='add-person__tree-name'>{unit.name}</span>
                          {selected && (
                            <Icon name='check' size={16} className='add-person__tree-check' />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && nodesMissing && (
                    <p className='add-person__error'>
                      <Icon name='circle-alert' size={14} />
                      {t('add_person_err_structure')}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* ---- ACCESS & TAGS ---- */}
            <section className='add-person__card'>
              <p className='add-person__card-head'>
                <Icon name='shield-check' size={16} />
                {t('add_person_section_access')}
              </p>

              <div className='add-person__card-body'>
                <div className='add-person__section'>
                  <p className='add-person__label'>
                    {t('add_person_roles_label')}
                    <span className='add-person__hint'> · {t('add_person_roles_hint')}</span>
                  </p>
                  <div className='add-person__pills'>
                    {selectableRoles.map((role) => {
                      const selected = draft.roleIds.includes(role.id);
                      return (
                        <button
                          key={role.id}
                          type='button'
                          className={`people-role-pill${selected ? ' people-role-pill--selected' : ''}`}
                          aria-pressed={selected}
                          onClick={() => toggle('roleIds', role.id)}
                        >
                          <Icon name={selected ? 'check' : 'shield-check'} size={15} />
                          {role.name}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && rolesMissing && (
                    <p className='add-person__error'>
                      <Icon name='circle-alert' size={14} />
                      {t('add_person_err_roles')}
                    </p>
                  )}
                </div>

                <div className='add-person__section'>
                  <p className='add-person__label'>
                    {t('add_person_tags_label')}
                    <span className='add-person__hint'> · {t('add_person_tags_hint')}</span>
                  </p>
                  <div className='add-person__pills'>
                    {peopleTags.map((tag) => {
                      const selected = draft.tagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type='button'
                          className={`people-role-pill${selected ? ' people-role-pill--selected' : ''}`}
                          aria-pressed={selected}
                          onClick={() => toggle('tagIds', tag.id)}
                        >
                          <Icon name={selected ? 'check' : 'tag'} size={15} />
                          {tag.title}
                        </button>
                      );
                    })}
                    {newTagOpen ? (
                      <Input
                        size='sm'
                        autoFocus
                        className='add-person__new-tag-input'
                        placeholder={t('add_person_tags_new_ph')}
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={onNewTagKeyDown}
                        onBlur={addTag}
                        disabled={addingTag}
                      />
                    ) : (
                      <button
                        type='button'
                        className='people-role-pill people-role-pill--dashed'
                        onClick={() => setNewTagOpen(true)}
                      >
                        <Icon name='plus' size={15} />
                        {t('add_person_tags_new')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default AddPeopleDrawer;
